import { useNavigate } from "react-router";
import { Check, Trash2, ExternalLink } from "lucide-react";
import {
  formatNotificationTime,
  getNotificationTypeConfig,
  getPriorityBadge,
} from "../../utils/notificationHelpers";

export default function NotificationItem({
  notification,
  onMarkAsRead,
  onDismiss,
  onNavigate,
  compact = false,
}) {
  const navigate = useNavigate();
  const {
    id,
    title,
    message,
    actor,
    notification_type,
    priority,
    action_url,
    is_read,
    created_at,
  } = notification;

  const typeConfig = getNotificationTypeConfig(notification_type);
  const IconComponent = typeConfig.icon;
  const priorityBadge = getPriorityBadge(priority);

  const handleClick = (e) => {
    // If clicking action buttons, do not trigger item click
    if (e.target.closest("button[data-action]")) {
      return;
    }

    if (!is_read && onMarkAsRead) {
      onMarkAsRead(id);
    }

    if (action_url) {
      if (onNavigate) onNavigate();
      if (action_url.startsWith("http://") || action_url.startsWith("https://")) {
        window.open(action_url, "_blank", "noopener,noreferrer");
      } else {
        navigate(action_url);
      }
    }
  };

  return (
    <div
      onClick={handleClick}
      className={`group relative flex items-start gap-3 transition-all duration-150 text-left ${compact ? "p-3" : "p-4 rounded-xl border"
        } ${!is_read
          ? "bg-primary/5 hover:bg-primary/10 border-primary/20"
          : "hover:bg-base-200/60 bg-base-100 border-base-200"
        } ${action_url ? "cursor-pointer" : "cursor-default"}`}
    >
      {/* Icon */}
      <div
        className={`shrink-0 w-9 h-9 rounded-xl flex items-center justify-center border shadow-2xs ${typeConfig.bgClass} ${typeConfig.borderClass}`}
      >
        <IconComponent className="w-4 h-4" strokeWidth={2.25} />
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0 pr-2">
        <div className="flex items-center gap-1.5 flex-wrap">
          <span
            className={`font-semibold text-sm truncate ${!is_read ? "text-base-content font-bold" : "text-base-content/80"
              }`}
          >
            {title}
          </span>

          {priorityBadge && (
            <span className={priorityBadge.className}>{priorityBadge.text}</span>
          )}

          {!is_read && (
            <span className="w-2 h-2 rounded-full bg-primary shrink-0 ml-auto" />
          )}
        </div>

        <p className="text-xs text-base-content/70 mt-1 line-clamp-2 leading-relaxed">
          {message}
        </p>

        {actor?.full_name && (
          <p className="text-[11px] text-base-content/50 mt-1">
            By <span className="font-medium text-base-content/70">{actor.full_name}</span>
          </p>
        )}

        <div className="flex items-center gap-2 mt-2">
          <span className="text-[11px] text-base-content/50">
            {formatNotificationTime(created_at)}
          </span>

          {action_url && (
            <span className="inline-flex items-center gap-1 text-[11px] font-medium text-primary hover:underline">
              <span>View details</span>
              <ExternalLink className="w-3 h-3" />
            </span>
          )}
        </div>
      </div>

      {/* Action buttons (hover or mobile visible) */}
      <div className="flex items-center gap-1 opacity-80 md:opacity-0 group-hover:opacity-100 transition-opacity shrink-0">
        {!is_read && onMarkAsRead && (
          <button
            type="button"
            data-action="mark-read"
            title="Mark as read"
            onClick={(e) => {
              e.stopPropagation();
              onMarkAsRead(id);
            }}
            className="p-1.5 rounded-lg text-base-content/60 hover:text-primary hover:bg-base-200 transition-colors"
          >
            <Check className="w-4 h-4" />
          </button>
        )}

        {onDismiss && (
          <button
            type="button"
            data-action="dismiss"
            title="Dismiss notification"
            onClick={(e) => {
              e.stopPropagation();
              onDismiss(id);
            }}
            className="p-1.5 rounded-lg text-base-content/60 hover:text-error hover:bg-error/10 transition-colors"
          >
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        )}
      </div>
    </div>
  );
}
